// @spec GHG-STR-010
/**
 * API client functions for GHG activity data endpoints
 */

import type {
  ActivityEstimatePreviewInput,
  ActivityEstimatePreviewResponse,
  ActivityDataListFilters,
  ActivityDataListResponse,
  ActivityDataResponse,
  CreateActivityDataInput,
  Scope1ActivityDataSummaryResponse,
  Scope2ActivityDataSummaryResponse,
  Scope3ActivityDataSummaryResponse,
  UpdateActivityDataInput,
} from '../types/activity-data';

import { createApiClient } from '@/lib/api-client';

export async function fetchActivityData(
  getToken: () => Promise<string | null>,
  organizationId: string,
  filters: ActivityDataListFilters
): Promise<ActivityDataListResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  const params = new URLSearchParams();
  if (filters.reportingPeriodId) {
    params.set('reportingPeriodId', filters.reportingPeriodId);
  }
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo);
  }
  if (filters.facilityId) {
    params.set('facilityId', filters.facilityId);
  }
  if (filters.activityGroupId) {
    params.set('activityGroupId', filters.activityGroupId);
  }
  if (filters.scope !== undefined) {
    params.set('scope', String(filters.scope));
  }
  if (filters.activityCategory) {
    params.set('activityCategory', filters.activityCategory);
  }
  if (filters.dataQuality) {
    params.set('dataQuality', filters.dataQuality);
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }
  if (filters.missingEmissions !== undefined) {
    params.set('missingEmissions', String(filters.missingEmissions));
  }
  if (filters.scope3Category !== undefined) {
    params.set('scope3Category', String(filters.scope3Category));
  }
  if (filters.scope1CategoryBucket) {
    params.set('scope1CategoryBucket', filters.scope1CategoryBucket);
  }
  if (filters.page) {
    params.set('page', String(filters.page));
  }
  if (filters.pageSize) {
    params.set('pageSize', String(filters.pageSize));
  }

  const queryString = params.toString();
  const url = `/organizations/${organizationId}/ghg/activity-data?${queryString}`;

  return apiClient.get<ActivityDataListResponse>(url);
}

export async function fetchScope1ActivityDataSummary(
  getToken: () => Promise<string | null>,
  organizationId: string,
  filters: ActivityDataListFilters
): Promise<Scope1ActivityDataSummaryResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  const params = new URLSearchParams();
  if (filters.reportingPeriodId) {
    params.set('reportingPeriodId', filters.reportingPeriodId);
  }
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo);
  }
  if (filters.facilityId) {
    params.set('facilityId', filters.facilityId);
  }
  if (filters.activityGroupId) {
    params.set('activityGroupId', filters.activityGroupId);
  }
  if (filters.activityCategory) {
    params.set('activityCategory', filters.activityCategory);
  }
  if (filters.dataQuality) {
    params.set('dataQuality', filters.dataQuality);
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }
  if (filters.missingEmissions !== undefined) {
    params.set('missingEmissions', String(filters.missingEmissions));
  }
  if (filters.scope1CategoryBucket) {
    params.set('scope1CategoryBucket', filters.scope1CategoryBucket);
  }

  const queryString = params.toString();
  const url = `/organizations/${organizationId}/ghg/activity-data/summary/scope1?${queryString}`;
  return apiClient.get<Scope1ActivityDataSummaryResponse>(url);
}

export async function fetchScope2ActivityDataSummary(
  getToken: () => Promise<string | null>,
  organizationId: string,
  filters: ActivityDataListFilters
): Promise<Scope2ActivityDataSummaryResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  const params = new URLSearchParams();
  if (filters.reportingPeriodId) {
    params.set('reportingPeriodId', filters.reportingPeriodId);
  }
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo);
  }
  if (filters.facilityId) {
    params.set('facilityId', filters.facilityId);
  }
  if (filters.activityGroupId) {
    params.set('activityGroupId', filters.activityGroupId);
  }
  if (filters.activityCategory) {
    params.set('activityCategory', filters.activityCategory);
  }
  if (filters.dataQuality) {
    params.set('dataQuality', filters.dataQuality);
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }
  if (filters.missingEmissions !== undefined) {
    params.set('missingEmissions', String(filters.missingEmissions));
  }
  if (filters.scope3Category !== undefined) {
    params.set('scope3Category', String(filters.scope3Category));
  }

  const queryString = params.toString();
  const url = `/organizations/${organizationId}/ghg/activity-data/summary/scope2?${queryString}`;
  return apiClient.get<Scope2ActivityDataSummaryResponse>(url);
}

export async function fetchScope3ActivityDataSummary(
  getToken: () => Promise<string | null>,
  organizationId: string,
  filters: ActivityDataListFilters
): Promise<Scope3ActivityDataSummaryResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  const params = new URLSearchParams();
  if (filters.reportingPeriodId) {
    params.set('reportingPeriodId', filters.reportingPeriodId);
  }
  if (filters.dateFrom) {
    params.set('dateFrom', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set('dateTo', filters.dateTo);
  }
  if (filters.facilityId) {
    params.set('facilityId', filters.facilityId);
  }
  if (filters.activityGroupId) {
    params.set('activityGroupId', filters.activityGroupId);
  }
  if (filters.activityCategory) {
    params.set('activityCategory', filters.activityCategory);
  }
  if (filters.dataQuality) {
    params.set('dataQuality', filters.dataQuality);
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }
  if (filters.missingEmissions !== undefined) {
    params.set('missingEmissions', String(filters.missingEmissions));
  }
  if (filters.scope3Category !== undefined) {
    params.set('scope3Category', String(filters.scope3Category));
  }

  const queryString = params.toString();
  const url = `/organizations/${organizationId}/ghg/activity-data/summary/scope3?${queryString}`;
  return apiClient.get<Scope3ActivityDataSummaryResponse>(url);
}

export async function fetchActivityDataEntry(
  getToken: () => Promise<string | null>,
  organizationId: string,
  entryId: string
): Promise<ActivityDataResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.get<ActivityDataResponse>(
    `/organizations/${organizationId}/ghg/activity-data/${entryId}`
  );
}

export async function createActivityData(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: CreateActivityDataInput
): Promise<ActivityDataResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.post<ActivityDataResponse>(
    `/organizations/${organizationId}/ghg/activity-data`,
    data
  );
}

export async function previewActivityEstimate(
  getToken: () => Promise<string | null>,
  organizationId: string,
  data: ActivityEstimatePreviewInput,
  options?: { signal?: AbortSignal }
): Promise<ActivityEstimatePreviewResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.post<ActivityEstimatePreviewResponse>(
    `/organizations/${organizationId}/ghg/activity-data/preview`,
    data,
    options
  );
}

export async function updateActivityData(
  getToken: () => Promise<string | null>,
  organizationId: string,
  entryId: string,
  data: UpdateActivityDataInput
): Promise<ActivityDataResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.put<ActivityDataResponse>(
    `/organizations/${organizationId}/ghg/activity-data/${entryId}`,
    data
  );
}

export async function deleteActivityData(
  getToken: () => Promise<string | null>,
  organizationId: string,
  entryId: string
): Promise<void> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  await apiClient.delete(`/organizations/${organizationId}/ghg/activity-data/${entryId}`);
}
